import React, { useRef, useCallback } from 'react';
import { Plus, X, Pencil, Loader2, ImagePlus } from 'lucide-react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNativePlatform } from '@/utils/capacitorUtils';
import { cn } from '@/lib/utils';

export interface PhotoEntry {
  id: string;          // stable ID — never changes after creation
  localUrl: string;
  uploadedUrl: string;
  isUploading: boolean;
}

interface MultiPhotoUploaderProps {
  photos: PhotoEntry[];
  onAdd: (file: File) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  maxPhotos?: number;
}

export const MultiPhotoUploader: React.FC<MultiPhotoUploaderProps> = ({
  photos,
  onAdd,
  onEdit,
  onDelete,
  maxPhotos = 5,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canAddMore = photos.length < maxPhotos;

  const handleNativeCamera = useCallback(async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
      });
      if (image.webPath) {
        const response = await fetch(image.webPath);
        const blob = await response.blob();
        const file = new File([blob], `photo.${image.format || 'jpg'}`, {
          type: blob.type || 'image/jpeg',
        });
        onAdd(file);
      }
    } catch {
      // user cancelled
    }
  }, [onAdd]);

  const handleWebPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleAddPhoto = useCallback(() => {
    if (isNativePlatform()) {
      handleNativeCamera();
    } else {
      handleWebPicker();
    }
  }, [handleNativeCamera, handleWebPicker]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const remaining = maxPhotos - photos.length;
      files.slice(0, remaining).forEach((file) => onAdd(file));
      e.target.value = '';
    },
    [onAdd, photos.length, maxPhotos]
  );

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="relative flex-shrink-0 w-28 h-28 rounded-xl overflow-hidden border-2 border-border snap-start"
          >
            <img
              src={photo.localUrl}
              alt="Snap photo"
              className="w-full h-full object-cover"
            />

            {photo.isUploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              </div>
            )}

            {!photo.isUploading && (
              <>
                <button
                  type="button"
                  onClick={() => onEdit(photo.id)}
                  className="absolute top-1.5 left-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 transition-colors"
                  aria-label="Edit photo"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(photo.id)}
                  className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                  aria-label="Remove photo"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        ))}

        {canAddMore && (
          <button
            type="button"
            onClick={handleAddPhoto}
            className={cn(
              'flex-shrink-0 w-28 h-28 rounded-xl border-2 border-dashed border-border',
              'flex flex-col items-center justify-center gap-1.5',
              'text-muted-foreground hover:border-[#cfef00] hover:text-[#cfef00]',
              'transition-colors snap-start'
            )}
          >
            {photos.length === 0 ? (
              <>
                <ImagePlus className="h-7 w-7" />
                <span className="text-xs font-medium">Add Photo</span>
              </>
            ) : (
              <>
                <Plus className="h-6 w-6" />
                <span className="text-xs">{photos.length}/{maxPhotos}</span>
              </>
            )}
          </button>
        )}
      </div>

      {photos.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Tap the pencil to edit &middot; {photos.length}/{maxPhotos} added
        </p>
      )}
    </div>
  );
};
